<template>
  <div class="container">
    <div class="trials-filter-container" :class="{ 'phase-tab-disabled': treatmentsSelected }">
      <div class="trial-phase" @click="phaseButtonClick(1)" :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="material-symbols-outlined size-20" :class="{ 'phase-tab-disabled': treatmentsSelected }">
          <span class="material-symbols-outlined" :class="{ 'phase-tab-disabled': treatmentsSelected }"
            :disabled="{ 'phase-tab-disabled': treatmentsSelected }">
            check_box{{ phaseIcon1 }}
          </span>
        </span>
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }"> Phase 1</span>
      </div>
      <div class="trial-phase" @click="phaseButtonClick(2)" :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="material-symbols-outlined size-20">
          <span class="material-symbols-outlined" :class="{ 'phase-tab-disabled': treatmentsSelected }">
            check_box{{ phaseIcon2 }}
          </span>
        </span>
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }"> Phase 2</span>
      </div>
      <div class="trial-phase" @click="phaseButtonClick(3)" :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="material-symbols-outlined size-20">
          <span class="material-symbols-outlined" :class="{ 'phase-tab-disabled': treatmentsSelected }">
            check_box{{ phaseIcon3 }}
          </span>
        </span>
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }"> Phase 3</span>
      </div>
      <div class="trial-phase" :class="{ 'phase-tab-disabled': treatmentsSelected }">
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }">Within </span>
        <input v-model="distance" type="number" class="distance-input"
          :class="{ 'phase-tab-disabled': treatmentsSelected }" min="1" max="24901">
        <span class="phase-text" :class="{ 'phase-tab-disabled': treatmentsSelected }">Miles</span>
      </div>
    </div>
    <div class="spinner-div" :class="{ 'spinner': spinnerOn }">
    </div>
    <div class="thera-label">
      {{ theraLabel }} <span class="count">{{ treatmentsSelected ? theraCount : filteredCount }}</span>
    </div>
    <div v-if="!showDetails" class="treatments-trials">
      <div v-if="treatmentsSelected" class="treatments-trials">
        <div v-for="treatment in activeTr" :key="treatment.id">
          <Treatment :name="treatment.name" :description="treatment.description" @open-details-modal="onDetailsModalOpen">
          </Treatment>
        </div>
      </div>
      <div v-else class="treatments-trials">
        <div v-for="trial in activeTr" :key="trial.id">
          <Trial :id="trial.id" :description="trial.briefTitle" :location="trial.locations" :phase="trial.phase"
            @open-details-modal="onDetailsModalOpen">
          </Trial>
        </div>
      </div>
    </div>
    <div v-else class="details-modal">
      <DetailsModal @close-details-modal="onDetailsModalClose" :title="showDetailsTitle" :text="showDetailsText"
        :type="showDetailsType">
      </DetailsModal>
    </div>
    <div class="pagination">
      <TreatmentsTrialsPagination :card-list="treatmentsSelected ? fullList : filteredTrialList"
        :set-page-one="setPageOne" @onCurrentPageChange="onCurrentPageUpdateActiveTr">
      </TreatmentsTrialsPagination>
    </div>
  </div>
</template>

<script setup>
// Copyright (c) 2020-2023 dātma, inc™
//
// Unauthorized copying of this file, via any medium is strictly prohibited
// Proprietary and confidential

import { computed, defineProps, onMounted, ref, toRaw, watch, watchEffect } from 'vue'
import Treatment from './Treatment.vue'
import Trial from './Trial.vue'
import TreatmentsTrialsPagination from './TreatmentsTrialsPagination.vue'
import DetailsModal from './DetailsModal.vue'
import { useStore } from 'vuex'
import { getTreatments, getTrials } from './getGenomicsData.js'

const MAX_CARDS = 6

const props = defineProps({
  treatments: Object,
  trials: Object,
  alterations: Array,
  // flag indicating the genomics variants are loading
  loadingGenomics: Boolean,
  // flag indicatin the variants and the treatments & trials for the first variant are still loading
  stillFetchingVariants: Boolean,
})

const showDetails = ref(false)
const showDetailsTitle = ref('')
const showDetailsText = ref('')
const showDetailsType = ref('')
const activeTr = ref({}) //represents the currently displayed slice of treatments/trials cards
const fullList = ref([]) //represents the full list of treatments or trials
const filteredTrialList = ref([]) //just for trial filtering based on phase and location
const theraCount = ref(0)
const filteredCount = computed(() => filteredTrialList.value?.length || 0) //just for trials
const phaseIcon1 = ref('')
const phaseIcon2 = ref('')
const phaseIcon3 = ref('')
const phase1Selected = ref(true)
const phase2Selected = ref(true)
const phase3Selected = ref(true)
const treatmentsSelected = ref(true)
const setPageOne = ref(false)
const distance = ref('')
const spinnerOn = ref(false)

const store = useStore()
const genomicsResults = computed(() => store.getters.session?.genomicsResults || {})

const phaseIconLookup = { 1: phaseIcon1, 2: phaseIcon2, 3: phaseIcon3 }
const phaseSelectedLookup = { 1: phase1Selected, 2: phase2Selected, 3: phase3Selected }

const onDetailsModalClose = () => {
  showDetails.value = false
}
const onDetailsModalOpen = (title, text, type) => {
  showDetailsTitle.value = title
  showDetailsText.value = text
  showDetails.value = true
  showDetailsType.value = type
}

const phaseButtonClick = (n) => {
  phaseIconLookup[n].value = phaseIconLookup[n].value === '' ? '_outline_blank' : ''
  phaseSelectedLookup[n].value = !phaseSelectedLookup[n].value
  filterTrialList()
  //because filtering should reduce page numbers, go to page 1
  onSetPageOne()
}

const onSetPageOne = () => {
  setPageOne.value = true
}

const theraLabel = computed(() => {
  return genomicsResults.value.treatmentOrTrialType === 'treatments' ? 'Therapies' : 'Trials'
})

const onCurrentPageUpdateActiveTr = (n) => {
  const sliceMin = MAX_CARDS * (n - 1)
  const sliceMax = sliceMin + MAX_CARDS
  if (genomicsResults.value.treatmentOrTrialType === 'treatments') {
    activeTr.value = fullList.value.slice(sliceMin, sliceMax)
  }
  else {
    activeTr.value = filteredTrialList.value.slice(sliceMin, sliceMax)
  }
  setPageOne.value = false
}

const filterTrialList = () => {
  // TODO: rework for canonical distance
  const compareDistance = (a, b) => {
    if (!a.locations || !b.locations) { return }
    if (!a.locations[0].distance && b.locations[0].distance) { return 1 }
    if (a.locations[0].distance && !b.locations[0].distance) { return -1 }
    if (a.locations[0]?.distance < b.locations[0]?.distance) {
      return -1
    }
    if (a.locations[0]?.distance > b.locations[0]?.distance) {
      return 1
    }
    return 0
  }

  let filterArray = []
  filterArray = filterArray.concat(toRaw(fullList.value))

  if (!phase1Selected.value) {
    filterArray = filterArray.filter((trial) => trial.phase !== 'Phase 1')
  }
  if (!phase2Selected.value) {
    filterArray = filterArray.filter((trial) => trial.phase !== 'Phase 2')
  }
  if (!phase3Selected.value) {
    filterArray = filterArray.filter((trial) => trial.phase !== 'Phase 3')
  }
  // for those Phase1/Phase2 and Phase2/Phase3 cases
  if (!phase1Selected.value && !phase2Selected.value) {
    filterArray = filterArray.filter((trial) => !trial.phase?.includes('1') && !trial.phase?.includes('2'))
  }
  if (!phase2Selected.value && !phase3Selected.value) {
    filterArray = filterArray.filter((trial) => !trial.phase?.includes('2') && !trial.phase?.includes('3'))
  }
  if (distance.value) {
    filterArray = filterArray.filter((trial) => Math.trunc(trial.locations[0]?.distance) <= distance.value)
  }

  filterArray = filterArray.toSorted(compareDistance)

  filteredTrialList.value = filterArray
  activeTr.value = filteredTrialList.value.slice(0, MAX_CARDS)
}

const populateFullList = async (treatmentOrTrialType, alter) => {
  if (!fullList.value.length || theraCount.value === 0) { return }

  // TODO: use actual distance/location
  const MAX_DISTANCE = '24000'
  const location = '97005'
  let accumArray = []
  spinnerOn.value = true
  accumArray = accumArray.concat(toRaw(fullList.value))
  for (let start = MAX_CARDS * 3; start < theraCount.value; start += MAX_CARDS * 3) {
    if (treatmentOrTrialType === 'treatments') {
      const _treatments = await getTreatments(MAX_DISTANCE, location, alter.gene, alter.originalAlt, start)
      accumArray = accumArray.concat(_treatments.rows.slice(0, MAX_CARDS * 3))
      fullList.value = accumArray
    }
    else {
      const _trials = await getTrials(MAX_DISTANCE, location, alter.gene, alter.originalAlt, start)
      accumArray = accumArray.concat(_trials.rows.slice(0, MAX_CARDS * 3))
      fullList.value = accumArray
      // looks better here, but may want to move it out of loop
      // for optimization
      filterTrialList()
    }
  }
  spinnerOn.value = false
}
// We're changing the selectedAlteration, or treatment or trial type.
// responding to user selection from the Variants icons
watch(() => (genomicsResults.value.selectedAlteration, genomicsResults.value.treatmentOrTrialType ), async () => {
  theraCount.value = genomicsResults.value.treatmentOrTrialCount
  treatmentsSelected.value = genomicsResults.value.treatmentOrTrialType === 'treatments'

  // shouldn't run if we're still loading genomics variants
  if (!props.alterations.length || props.stillFetchingVariants || props.loadingGenomics) { return }

  for (const alter of props.alterations) {
    if (genomicsResults.value.selectedAlteration === alter.originalAlt) {
      if (genomicsResults.value.treatmentOrTrialType === 'treatments') {
        activeTr.value = alter.treatmentRows.slice(0, MAX_CARDS)
        // store off the first 3 pages, since we have them (20 from annotation)
        // TODO: may be able to make this cleaner depending on backend API support
        fullList.value = alter.treatmentRows.slice(0, MAX_CARDS * 3)
        await populateFullList('treatments', alter)
      }
      else {
        activeTr.value = alter.trialRows.slice(0, MAX_CARDS)
        // store off the first 3 pages, since we have them
        // TODO: may be able to make this cleaner depending on backend API support
        fullList.value = alter.trialRows.slice(0, MAX_CARDS * 3)
        await populateFullList('trials', alter)
      }
    }
  }
},
// This watch won't occur without this deep watcher syntax
// https://vuejs.org/guide/essentials/watchers.html#deep-watchers
{ deep: true }
)

// currently, display the first page of treatments for the top alteration
// after all of the alteration fetching is done and we have the first set of
// treatments and trials
watch(() => props.stillFetchingVariants, async (stillFetchingVariants) => {
  if (!props.alterations.length || stillFetchingVariants) { return }
  const firstAlter = props.alterations[0]
  activeTr.value = firstAlter.treatmentRows.slice(0, 6)
  const cardState = {
    sampleId: genomicsResults.value.sampleId || '',
    diagnosis: genomicsResults.value.diagnosis || '',
    //specimen type, testType, labName probably need to go here as well
    selectedAlteration: props.alterations[0].originalAlt || '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: props.alterations[0].treatmentsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
  await populateFullList('treatments', firstAlter)
})

// we've selected a new sample, so the intention here is to
// clear stuff out
watch(() => props.loadingGenomics, (loadingGenomics) => {
  if (!loadingGenomics) { return }
  activeTr.value = []
  fullList.value = []
  filteredTrialList.value = []
  theraCount.value = 0
  const cardState = {
    sampleId: genomicsResults.value.sampleId || '',
    diagnosis: genomicsResults.value.diagnosis || '',
    //specimen type, testType, labName probably need to go here as well
    selectedAlteration: '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: 0,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
})

watchEffect(() => {
  // not filtering treatements based on distance, just trials
  if (treatmentsSelected.value) { return }
  filterTrialList()
  onSetPageOne()
})

//lets get our selection back if we refresh
onMounted(() => {
  if (!props.alterations.length) { return }
  activeTr.value = props.alterations[0].treatmentRows.slice(0, 6)
  const cardState = {
    sampleId: genomicsResults.value.sampleId || '',
    diagnosis: genomicsResults.value.diagnosis || '',
    //specimen type, testType, labName probably need to go here as well
    selectedAlteration: props.alterations[0].originalAlt || '',
    treatmentOrTrialType: 'treatments',
    treatmentOrTrialCount: props.alterations[0].treatmentsCount,
  }
  store.dispatch('setGenomicsResults', { genomicsResults: cardState, publish: true })
})

</script>

<style lang="scss" scoped>
.container {
  margin-left: 15px;
}

.treatments-trials {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  height: 380px;
  overflow: auto;
  justify-content: flex-start;
  align-content: flex-start;
  gap: 15px 0;
}

.details-modal {
  margin-left: 10px;
  margin-bottom: 30px;
  flex-direction: row;
  flex-wrap: wrap;
  width: auto;
  height: 360px;
  width: 400px;
  background-color: #F7F9FB;
  overflow: auto;
  border-radius: 9.72px;
  box-shadow: 0 0 6px #b9b9b9;
}

.pagination {
  margin-top: 10px;
  // margin-left: -5px;
  display: flex;
  color: var(--Grey600)
}


.phase-text {
  margin: 0 3px;
}

.phase-tab-disabled {
  user-select: none;
  color: var(--Grey200);
  cursor: default;

  &:hover {
    cursor: default;
  }

  &>input {
    background-color: var(--Grey200);
  }
}

.thera-label {
  margin: 1px 0 3px 10px;
  color: var(--Grey500);
  width: fit-content;
  height: 16.2px;
  flex-shrink: 0;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.04px;

  &>.count {
    font-weight: 700;
    min-width: 15px;
    padding: 4px;
    color: #fff;
    background-color: var(--Secondary);
    align-items: center;
    justify-content: center;
    font-size: 0.6rem;
    font-weight: 800;
    border-radius: 10px;
    margin-left: 0;
    margin-top: -12px;
  }
}

.trials-filter-container {
  display: flex;
  color: var(--Grey500);
  font-size: 60%;
  align-items: left;
  justify-content: left;
  text-align: left;
  user-select: none;
}

.trial-phase {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  margin: 15px 0 15px 15px;
  background-color: var(--Grey100);
  padding: 4px;
  border-radius: 5px;
  text-align: center;
  color: var(--Grey600);

  cursor: pointer;

  &:hover {
    color: var(--Black);
  }
}

.distance-input {
  border: none;
  margin: 5px;
  width: 58px;
  height: 20px;
  border-radius: 6px;
  background: var(--card-label, #E5ECF6);
}

*:disabled {
  background-color: dimgrey;
  color: linen;
  opacity: 1;
}

.spinner-div {
  border-image: conic-gradient(from var(--angle), var(--MarketingMagenta), var(--BlendedOrange), var(--MarketingOrange), var(--BlendedOrange), var(--MarketingMagenta)) 1;
  transition: all linear 300ms;
}
</style>

<style type="text/css" scoped>
.spinner {
  --angle: 360deg;
  border-bottom: 1vmin solid;
  border-image: conic-gradient(from var(--angle), #e901be, #f46d4e, #ffaa0f, #f46d4e, #e901be) 1;
  animation: 1s rotate linear infinite;
}

@keyframes rotate {
  to {
    --angle: 0deg;
  }
}

@property --angle {
  syntax: '<angle>';
  initial-value: 0deg;
  inherits: true;
}
</style>
